import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';

import { PATH } from '../../constants/path';
import { HEADER_MOBILE_MEDIA_QUERY, HEADER_DESKTOP_MEDIA_QUERY } from '../../constants';
import { SUB_NAV_CATEGORIES } from '../../services/nav/constants';
import { MENU_VERSION_MENU_MOBILE_SLIDE } from '../../services/header/constants';

import { showSubNav, hideSubNav } from '../../services/nav/actions';
import { getOrderedCategories } from '../../services/category/selector';

import LocalizedNavLink from '../router/LocalizedNavLink';
import CategoriesSubNavComponent from './CategoriesSubNavComponent';

import './NavComponent.scss';

// Ids lien déclencheur / sous-menu par type d'offre (aria-controls, retour de focus).
const SUB_NAV_IDS = {
    online: { link: 'nav-link-online', subNav: 'sous-menu-rubriques-online' },
    ebon: { link: 'nav-link-ebon', subNav: 'sous-menu-rubriques-ebon' },
    offline: { link: 'nav-link-offline', subNav: 'sous-menu-rubriques-offline' }
};

class MenuComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isOffline: false,
            isEbon: false
        };
    }

    toggleOffline = value => {
        this.setState({ isOffline: value });
    };
    toggleEbon = value => {
        this.setState({ isEbon: value });
    };

    showSubNav = subNav => this.props.showSubNav(subNav);
    hideSubNav = () => this.props.hideSubNav();

    onMouseEnterCategories = () => this.showSubNav(SUB_NAV_CATEGORIES);
    onMouseLeaveCategories = () => this.hideSubNav();

    showCategories = (isOffline, isEbon) => () => {
        this.toggleOffline(isOffline);
        this.toggleEbon(isEbon);
        this.onMouseEnterCategories();
    };

    // Ouverture au focus (desktop), ignorée lors du retour de focus après Escape.
    showCategoriesOnFocus = (isOffline, isEbon) => () => {
        if (!this.ignoreNextFocus) {
            this.showCategories(isOffline, isEbon)();
        }
    };

    onClickCategories = (matches, isOffline, isEbon) => e => {
        if (!matches) {
            this.toggleOffline(isOffline);
            this.toggleEbon(isEbon);
            e.preventDefault();
            if (!this.props.subNavDisplayed) {
                this.showSubNav(SUB_NAV_CATEGORIES);
            } else {
                this.hideSubNav();
            }
        }
    };

    makeOnClickCategories = (matches, isOffline, isEbon) =>
        this.onClickCategories(matches, isOffline, isEbon);

    // Type d'offre du sous-menu actif.
    getCurrentSubNavKey = () => {
        const { isOffline, isEbon } = this.state;
        return isOffline ? 'offline' : isEbon ? 'ebon' : 'online';
    };

    // Ferme le sous-menu si le focus sort du groupe lien + sous-menu.
    handleSubNavBlur = e => {
        const next = e.relatedTarget;
        if (next && next.closest && next.closest('.sous-menu-rubriques')) {
            return;
        }
        this.hideSubNav();
    };

    // Escape : ferme et rend le focus au lien déclencheur.
    handleSubNavKeyDown = e => {
        if (e.key === 'Escape' && this.props.subNavDisplayed) {
            this.hideSubNav();
            const trigger = document.getElementById(SUB_NAV_IDS[this.getCurrentSubNavKey()].link);
            if (trigger) {
                // focus() déclenche onFocus de façon synchrone.
                this.ignoreNextFocus = true;
                trigger.focus();
                this.ignoreNextFocus = false;
            }
        }
    };

    // Desktop : trois sous-menus montés, pilotés par `open` ; mobile : type courant seul (Redux).
    renderSubNav = (key, ariaLabel, typeProps = {}) => {
        const { categories, subNavDisplayed } = this.props;
        const isCurrent = this.getCurrentSubNavKey() === key;

        return (
            <React.Fragment>
                <MediaQuery query={HEADER_DESKTOP_MEDIA_QUERY}>
                    {matches =>
                        matches && (
                            <CategoriesSubNavComponent
                                categories={categories}
                                id={SUB_NAV_IDS[key].subNav}
                                open={subNavDisplayed && isCurrent}
                                ariaLabel={ariaLabel}
                                {...typeProps}
                            />
                        )
                    }
                </MediaQuery>
                <MediaQuery query={HEADER_MOBILE_MEDIA_QUERY}>
                    {matches =>
                        matches &&
                        isCurrent && (
                            <CategoriesSubNavComponent
                                categories={categories}
                                id={SUB_NAV_IDS[key].subNav}
                                ariaLabel={ariaLabel}
                                {...typeProps}
                            />
                        )
                    }
                </MediaQuery>
            </React.Fragment>
        );
    };

    render() {
        const {
            i18n,
            i18nAccessibility,
            stateOpenMenuMobileSlide,
            versionOpenMenuMobileSlide,
            subNavDisplayed,
            location
        } = this.props;
        const currentSubNav = this.getCurrentSubNavKey();

        const OnlineDirectoryUrl = `/${PATH.ONLINE}/`;
        const OfflineDirectoryUrl = `/${PATH.OFFLINE}/`;
        const EbonDirectoryUrl = `/${PATH.EBON}/`;

        return (
            <nav
                className={`menu menu-mobile-slide ${
                    stateOpenMenuMobileSlide &&
                    versionOpenMenuMobileSlide === MENU_VERSION_MENU_MOBILE_SLIDE
                        ? 'open'
                        : ''
                }`}
                id="menu">
                <div
                    className="conteneur menu-conteneur flex-container-h"
                    onBlur={this.handleSubNavBlur}
                    onKeyDown={this.handleSubNavKeyDown}>
                    <LocalizedNavLink
                        to={PATH.HOME_PATH}
                        activeClassName="actif"
                        name="headerNav-toHome"
                        exact>
                        {i18n.HOME}
                    </LocalizedNavLink>
                    <LocalizedNavLink
                        to={PATH.HOW_IT_WORK_PATH}
                        activeClassName="actif"
                        name="headerNav-toHowItWorks">
                        {i18n.HOW_IT_WORKS}
                    </LocalizedNavLink>

                    {/* En ligne : survol / focus ouvrent en desktop, clic bascule en mobile. */}
                    <MediaQuery query={HEADER_DESKTOP_MEDIA_QUERY}>
                        {matches => (
                            <LocalizedNavLink
                                id={SUB_NAV_IDS.online.link}
                                to={PATH.CASHBACK_ONLINE_MOST_POPULAR_PATH}
                                className={`menu-sous-menu ${
                                    location.pathname.indexOf(OnlineDirectoryUrl) !== -1
                                        ? 'actif'
                                        : ''
                                }`}
                                name="headerNav-toMostPopularCashback"
                                aria-haspopup="true"
                                aria-expanded={subNavDisplayed && currentSubNav === 'online'}
                                aria-controls={SUB_NAV_IDS.online.subNav}
                                onMouseEnter={
                                    matches ? this.showCategories(false, false) : undefined
                                }
                                onMouseLeave={matches ? this.onMouseLeaveCategories : undefined}
                                onFocus={
                                    matches ? this.showCategoriesOnFocus(false, false) : undefined
                                }
                                onClick={this.makeOnClickCategories(matches, false, false)}>
                                {i18n.ONLINE_OFFERS}
                            </LocalizedNavLink>
                        )}
                    </MediaQuery>
                    {this.renderSubNav('online', i18nAccessibility.NAV_SUBMENU_ONLINE_LABEL)}

                    {/* Offres en bon d'achat */}
                    <MediaQuery query={HEADER_DESKTOP_MEDIA_QUERY}>
                        {matches => (
                            <LocalizedNavLink
                                id={SUB_NAV_IDS.ebon.link}
                                to={PATH.CASHBACK_EBON_MOST_POPULAR_PATH}
                                className={`menu-sous-menu ${
                                    location.pathname.indexOf(EbonDirectoryUrl) !== -1
                                        ? 'actif'
                                        : ''
                                }`}
                                name="headerNav-toMostPopularEbon"
                                aria-haspopup="true"
                                aria-expanded={subNavDisplayed && currentSubNav === 'ebon'}
                                aria-controls={SUB_NAV_IDS.ebon.subNav}
                                onMouseEnter={
                                    matches ? this.showCategories(false, true) : undefined
                                }
                                onMouseLeave={matches ? this.onMouseLeaveCategories : undefined}
                                onFocus={
                                    matches ? this.showCategoriesOnFocus(false, true) : undefined
                                }
                                onClick={this.makeOnClickCategories(matches, false, true)}>
                                {i18n.EBON_OFFERS}
                            </LocalizedNavLink>
                        )}
                    </MediaQuery>
                    {this.renderSubNav('ebon', i18nAccessibility.NAV_SUBMENU_EBON_LABEL, {
                        isEbon: true
                    })}

                    {/* Offres en magasin */}
                    <MediaQuery query={HEADER_DESKTOP_MEDIA_QUERY}>
                        {matches => (
                            <LocalizedNavLink
                                id={SUB_NAV_IDS.offline.link}
                                to={PATH.CASHBACK_OFFLINE_MOST_POPULAR_PATH}
                                title={i18n.OFFLINE_OFFERS}
                                className={`menu-sous-menu ${
                                    location.pathname.indexOf(OfflineDirectoryUrl) !== -1
                                        ? 'actif'
                                        : ''
                                }`}
                                aria-haspopup="true"
                                aria-expanded={subNavDisplayed && currentSubNav === 'offline'}
                                aria-controls={SUB_NAV_IDS.offline.subNav}
                                onMouseEnter={
                                    matches ? this.showCategories(true, false) : undefined
                                }
                                onMouseLeave={matches ? this.onMouseLeaveCategories : undefined}
                                onFocus={
                                    matches ? this.showCategoriesOnFocus(true, false) : undefined
                                }
                                onClick={this.makeOnClickCategories(matches, true, false)}>
                                {i18n.OFFLINE_OFFERS}
                            </LocalizedNavLink>
                        )}
                    </MediaQuery>
                    {this.renderSubNav('offline', i18nAccessibility.NAV_SUBMENU_OFFLINE_LABEL, {
                        isOffline: true
                    })}

                    <LocalizedNavLink
                        to={PATH.REDUCTION_MOST_POPULAR_PATH}
                        activeClassName="actif"
                        name="headerNav-toMostPopularPromo">
                        {i18n.REDUCTION}
                    </LocalizedNavLink>
                    <LocalizedNavLink
                        to={PATH.TOOLBAR_PATH}
                        activeClassName="actif"
                        name="headerNav-toToolbarPage">
                        {i18n.TOOLBAR}
                    </LocalizedNavLink>
                    <LocalizedNavLink
                        to={PATH.CONTACTREQUEST_PATH}
                        activeClassName="actif"
                        name="headerNav-toContactPage">
                        {i18n.CONTACT}
                    </LocalizedNavLink>
                </div>
            </nav>
        );
    }
}

MenuComponent.propTypes = {
    stateOpenMenuMobileSlide: PropTypes.bool.isRequired,
    versionOpenMenuMobileSlide: PropTypes.string.isRequired,
    i18n: PropTypes.object.isRequired,
    i18nAccessibility: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    subNavDisplayed: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
    return {
        stateOpenMenuMobileSlide: state.header.stateOpenMenuMobileSlide,
        versionOpenMenuMobileSlide: state.header.versionOpenMenuMobileSlide,
        i18n: state.i18n.feature.header.menu,
        i18nAccessibility: state.i18n.feature.header.ACCESSIBILITY,
        categories: getOrderedCategories(state),
        subNavDisplayed: !!state.nav.subNav,
        location: state.routing.location
    };
};

const mapDispatchToProps = {
    showSubNav,
    hideSubNav
};

export default connect(mapStateToProps, mapDispatchToProps)(MenuComponent);



// WEBPACK FOOTER //
// ./src/components/header/NavComponent.js